import { AgentAccessRevocationModel, AgentRegistryRequestModel, Application } from '@/api';
import {
AgentGroupDimension,
AgentScope,
ApplyPoliciesRequest,
EstateAgentListParams,
EstateAgentQuery,
GenerateDraftPolicyRequest,
PolicyReviewQuery,
PolicyImplementationsQuery,
ReapplyPolicyDefinitionRequest,
RetryPolicyDeploymentRequest,
UpdateNativePolicyRequest,
ScopedPolicyCatalogQuery,
StartAgentDiscoveryRequest,
StartAgentOnboardingRequest,
TestAgentEnvironmentConnectionRequest,
TestAgentIdentityConnectionRequest,
UpdateAgentMissionRequest,
EffectiveEnforcementQuery,
} from '@/api/temp/AgentEstateService';
import type {
AgentPoliciesQuery,
CreateAgentPolicyRequest,
DeleteAgentPolicyRequest,
UpdateAgentPolicyRequest,
} from '@/api/temp/AgentPoliciesService';
import type {
  AgentGovernanceScenario,
  DataGovernanceScenario,
  FleetDimension,
} from '@/api/temp/GovernanceOverviewService';
import type {
CreateProfileRequest,
ProfileInput,
ProfileListQuery,
ProfileSelectionContext,
UpdateProfileRequest,
ValidateProfileRequest,
} from '@/api/temp/OAuthClientProfileService';
import type {
AgentAccessFlowListParams,
EnforcePolicyParams,
EstateMetricId,
GuardianContextKind,
} from '@/api/temp/TrustAIExecutiveService';
import { agentAttributeStore } from './agent-estate/attributes';
import { authoredAgentPolicyStore } from './agent-estate/authoredPolicies';
import { enforcementEnvironmentFor } from './agent-estate/enforcementReadiness';
import { toEstateAgentQuery } from './agent-estate/listParams';
import { getPlatformCatalog } from './agent-estate/seed';
import { agentEstateStore } from './agent-estate/store';
import * as protectionRecommendations from './agent-protection/recommendationRouting';
import { agentProtectionMocks } from './agent-protection/registry';
import { agentProtectionStore } from './agent-protection/store';
import { agentRegistryStore } from './agent-registry/store';
import configuration from './agents/configuration';
import purposes from './agents/purposes';
import { applicationStore } from './applications/store';
import { createEnterprisePolicyUsingPostMock } from './enterprise-policies/create';
import { enterprisePoliciesStore } from './enterprise-policies/store';
import { lakehouseStore } from './lakehouse/store';
import { ulhEntitlementAttributes } from './lakehouse/userAttributes';
import { accessPolicies,agentAttributes,conditionAttributes,conditionAttributesForApplication,mcpServerDiscovery,mcpUser,objectNames,userAttributes } from './mcp/data';
import policyResources from './metadata/policy_resources/data';
import { oauthClientProfileStore } from './oauth-client-profiles/store';
import {
  buildAgentActivitySummary,
  buildAgentFleetProtection,
  buildDataGovernanceSummary,
} from './trustai-executive/governanceFixtures';
import { trustAiExecutiveStore } from './trustai-executive/store';

/**
 * Registry mapping API method names to their mock data.
 * Add new entries here when creating mock data for an endpoint.
 * Naming convention: key = method name (e.g. method.name), value = imported data array.
 * The data file should live at a path mirroring the API URL:
 *   /api/metadata/{appId}/policy_resources → mock-data/metadata/policy_resources/data.ts
 */
export const mockRegistry: Record<string, unknown | ((params: unknown) => unknown)> = {
  getPolicyResourcesUsingGet: policyResources,
  getMcpUserUsingGet: mcpUser,
  getConditionAttributesUsingGet: conditionAttributes,
  getApplicationUserAttributesMetadataUsingGet: userAttributes,
  // Account/semantic context: UserEntitlementAttributeControllerService.getUserAttributesMetadataUsingGet
  getUserAttributesMetadataUsingGet: userAttributes,
  getConditionAttributesForApplicationUsingGet: conditionAttributesForApplication,
  listAttributesUsingGet: agentAttributes,
  listPurposesUsingGet: purposes,
  getChildrenUsingGet: [],
  getMcpServerNamesUsingGet: mcpServerDiscovery,
  listPolicyResourcesUsingGet: () => {
    const mcp = applicationStore.list({ type: "MCP_SERVER" })[0] as Application & { resources?: unknown[] };
    return { items: mcp?.resources ?? [] };
  },
  // Demo-only: Unified Lakehouse User Attributes tab has no real backend endpoint yet.
  getUnifiedLakehouseUserAttributesUsingGet: ulhEntitlementAttributes,
  getColumnsUsingGet: objectNames,
  "/api/metadata/8a488489887a8d360188bdb6ed9c2a84/mcpServerName": mcpServerDiscovery,
  // getDiscoveredMcpServersUsingGet: discoveredMcpServers,

  // Agent registry (TRUST-13366). Real endpoints - mocked inside <MockScope>
  // (`isAgentsMock`), where demo environments read as registries too.
  listAgentRegistriesUsingGet: () => agentRegistryStore.list(),
  validateUsingPost: (params: { body: AgentRegistryRequestModel }) =>
    agentRegistryStore.validate(params),
  previewProjectsUsingPost: (params: { body: AgentRegistryRequestModel }) =>
    agentRegistryStore.preview(params),
  importRegistryUsingPost: (params: { body: AgentRegistryRequestModel }) =>
    agentRegistryStore.onboard(params),
  syncAgentsUsingPost: (params: { id: string }) =>
    agentRegistryStore.importAgents(params),
  deleteRegistryUsingDelete: (params: { id: string }) =>
    agentRegistryStore.remove(params),
  syncAttributesUsingPost: (params: { id: string }) =>
    agentRegistryStore.syncAttributes(params),

  // AI Agent Estate onboarding preview (isNewAgentOnboarding). No backend yet -
  // every entry delegates to the single in-memory agentEstateStore so the list,
  // the group counts and the progress panel can never disagree.
  listAgentPlatformCatalogUsingGet: () => getPlatformCatalog(),
  listAgentEnvironmentsUsingGet: () => agentEstateStore.listEnvironments(),
  testAgentEnvironmentConnectionUsingPost: (
    params: TestAgentEnvironmentConnectionRequest,
  ) => agentEstateStore.testConnection(params),
  testAgentIdentityConnectionUsingPost: (
    params: TestAgentIdentityConnectionRequest,
  ) => agentEstateStore.testIdentityConnection(params),
  startAgentOnboardingUsingPost: (params: StartAgentOnboardingRequest) =>
    agentEstateStore.startOnboarding(params),
  disconnectAgentEnvironmentUsingDelete: (params: { id: string }) =>
    agentEstateStore.disconnect(params.id),
  listEstateAgentAttributesUsingGet: (params: { agentId: string }) =>
    agentAttributeStore.list(params),
  updateEstateAgentAttributesUsingPatch: (params: {
    agentId: string;
    values: Record<string, unknown>;
  }) => agentAttributeStore.update(params),
  getAgentOnboardingProgressUsingGet: (params: { environmentId: string }) =>
    agentEstateStore.getProgress(params.environmentId),
  getAgentEstateSummaryUsingGet: (
    params: {
      environmentId: string;
      dimension: AgentGroupDimension;
    } & Omit<EstateAgentQuery, 'environmentId'>,
  ) => agentEstateStore.getSummary(params),
  // Real endpoint (AgentControllerService); the store keeps its own query shape.
  listEstateAgentsUsingGet: (params: EstateAgentListParams) =>
    agentEstateStore.listAgents(toEstateAgentQuery(params)),
  // By agent id alone, as the live API: whichever environment has the agent.
  getEstateAgentUsingGet: (params: { agentId: string }) =>
    agentEstateStore.findAgent(params.agentId),
  // The seeded Access & Protection agent's recommendations come from the
  // protection store, so they target its own MCP inventory; every other scope
  // reads the estate engine.
  resolveAgentPolicyRecommendationsUsingPost: (params: {
    environmentId: string;
    scope: AgentScope;
  }) => {
    const estate = agentEstateStore.resolveRecommendations(
      params.environmentId,
      params.scope,
    );
    const seeded = agentProtectionStore.resolveRecommendations(params);
    // Every scope but the seeded agent reads the estate engine untouched.
    if (!seeded) return estate;
    // Merged, never replaced: the agent keeps the estate's recommendations
    // (and the native-policy flow behind them) next to the fixture's.
    return Promise.resolve(estate).then(items => [...seeded, ...items]);
  },
  resolveAgentPolicyCoverageUsingPost: (params: {
    environmentId: string;
    scope: AgentScope;
  }) =>
  {
    const estate = agentEstateStore.resolveCoverage(
      params.environmentId,
      params.scope,
    );
    const seeded = agentProtectionStore.resolveCoverage(params);
    return seeded
      ? { ...estate, policies: [...seeded.policies, ...estate.policies] }
      : estate;
  },
  resolvePolicyCatalogUsingPost: (params: ScopedPolicyCatalogQuery) =>
    agentEstateStore.resolvePolicyCatalog(params),
  // Split between the protection and estate stores, then merged.
  getPolicyReviewUsingPost: (params: PolicyReviewQuery) =>
    protectionRecommendations.getPolicyReview(params),
  generateDraftPolicyUsingPost: (params: GenerateDraftPolicyRequest) =>
    agentEstateStore.generateDraftPolicy(params),
  previewAgentPoliciesUsingPost: (params: ApplyPoliciesRequest) =>
    protectionRecommendations.previewPolicies(params),
  applyPoliciesUsingPost: (params: ApplyPoliciesRequest) => {
    if (!params.previewId) throw new Error("Review the policy scope before applying.");
    const result = protectionRecommendations.applyPolicies(params);
    // Throw rather than resolve undefined - otherwise the mutation reports
    // success (and toasts it) for policies it never applied.
    if (!result) {
      throw new Error(
        `Unknown mock policy or environment: ${params.policyIds.join(', ')}`,
      );
    }
    return result;
  },
  // No environment in the body; use the latest one, like `useOnboardingEnvironment`.
  setAgentAccessRevocationUsingPost: (params: { body: AgentAccessRevocationModel }) =>
    agentEstateStore.setAccessRevocation({
      ...params.body,
      environmentId: agentEstateStore.listEnvironments().at(-1)?.id ?? '',
    }),
  updateAgentMissionUsingPost: (params: UpdateAgentMissionRequest) => {
    const result = agentEstateStore.updateMission(params);
    // Throw rather than resolve undefined - otherwise the mutation reports
    // success (and toasts it) for a mission it never wrote.
    if (!result) {
      throw new Error(`Unknown mock agent or environment: ${params.agentId}`);
    }
    return result;
  },
  getEffectiveEnforcementUsingGet: (params: EffectiveEnforcementQuery) =>
    agentEstateStore.getEffectiveEnforcement(params),
  getEnforcementEnvironmentUsingGet: () => enforcementEnvironmentFor(),
  startAgentDiscoveryUsingPost: (params: StartAgentDiscoveryRequest) =>
    agentEstateStore.startDiscovery(params),
  getAgentDiscoveryUsingGet: (params: { discoveryId: string }) =>
    agentEstateStore.getDiscovery(params.discoveryId),
  listDiscoveredProjectAgentsUsingGet: (params: {
    discoveryId: string;
    projectId: string;
  }) => agentEstateStore.listProjectAgents(params.discoveryId, params.projectId),
  // OAuth Client Profiles (Administration → AI Policy Enforcement). One
  // store behind the admin page and the Authentication Setup picker.
  listOAuthClientProfilesUsingGet: (params: ProfileListQuery) =>
    oauthClientProfileStore.list(params),
  getOAuthClientProfileUsingGet: (params: { id: string }) =>
    oauthClientProfileStore.get(params.id),
  getOAuthClientProfileFormOptionsUsingGet: () =>
    oauthClientProfileStore.formOptions(),
  listOAuthAuthorizationProvidersUsingGet: () =>
    oauthClientProfileStore.listProviders(),
  getOAuthAuthorizationProviderUsingGet: (params: { id: string }) =>
    oauthClientProfileStore.getProvider(params.id),
  createOAuthClientProfileUsingPost: (params: CreateProfileRequest) =>
    oauthClientProfileStore.create(params),
  updateOAuthClientProfileUsingPatch: (params: UpdateProfileRequest) =>
    oauthClientProfileStore.update(params),
  validateOAuthClientProfileUsingPost: (params: ValidateProfileRequest) =>
    oauthClientProfileStore.validate(params),
  getOAuthClientProfileValidationUsingGet: (params: { jobId: string }) =>
    oauthClientProfileStore.getValidation(params.jobId),
  cancelOAuthClientProfileValidationUsingPost: (params: { jobId: string }) =>
    oauthClientProfileStore.cancelValidation(params.jobId),
  getOAuthClientProfileUsageUsingGet: (params: { id: string }) =>
    oauthClientProfileStore.usage(params.id),
  previewOAuthClientProfileImpactUsingPost: (params: {
    id: string;
    input: ProfileInput;
  }) => oauthClientProfileStore.previewImpact(params),
  archiveOAuthClientProfileUsingPost: (params: { id: string; revision: string }) =>
    oauthClientProfileStore.archive(params),
  restoreOAuthClientProfileUsingPost: (params: { id: string; revision: string }) =>
    oauthClientProfileStore.restore(params),
  listOAuthClientProfileOptionsUsingPost: (params: ProfileSelectionContext) =>
    oauthClientProfileStore.options(params),
  listPolicyImplementationsUsingGet: (params: PolicyImplementationsQuery) =>
    agentEstateStore.listPolicyImplementations(params),
  // The mutations below throw on anything they cannot do - an unapplied
  // definition, an in-flight deployment, unacknowledged overrides - so the
  // mutation never reports a false success.
  updateNativePolicyUsingPut: (params: UpdateNativePolicyRequest) =>
    agentEstateStore.updateNativePolicy(params),
  reapplyPolicyDefinitionUsingPost: (params: ReapplyPolicyDefinitionRequest) =>
    agentEstateStore.reapplyPolicyDefinition(params),
  retryPolicyDeploymentUsingPost: (params: RetryPolicyDeploymentRequest) =>
    agentEstateStore.retryFailedDeployment(params),
  // Agent Access & Protection. Mock-only, see AGENT_FEATURES.accessProtection.
  ...agentProtectionMocks,
  // Authored agent policies and tool customizations; live goes through
  // AuthoredPolicyApi.
  listAgentPoliciesUsingGet: (params: AgentPoliciesQuery) =>
    authoredAgentPolicyStore.list(params),
  createAgentPolicyUsingPost: (params: CreateAgentPolicyRequest) =>
    authoredAgentPolicyStore.create(params),
  updateAgentPolicyUsingPut: (params: UpdateAgentPolicyRequest) =>
    authoredAgentPolicyStore.update(params),
  deleteAgentPolicyUsingDelete: (params: DeleteAgentPolicyRequest) =>
    authoredAgentPolicyStore.delete(params),

  listApplicationsUsingGet: (params: { type: string }) =>
    applicationStore.list({ type: params.type }),
  createUsingPost: createEnterprisePolicyUsingPostMock,
  listUsingGet: (params?: {
    enabled?: boolean;
    name?: string;
    page?: number;
    size?: number;
    status?: string;
  }) => enterprisePoliciesStore.list(params),

  getApplicationConfigurationAllFeatureUsingGet: (params: { id: string }) => {
    if (params.id === "8a48827e9b9deea0019b9fcffbee2ed4") {
      return configuration;
    }
    return undefined;
  },
  getApplicationUsingGet: (params: { id: string }) => applicationStore.get(params.id),
  // Mutation mocks: route through the per-entity store so the list reflects new/updated/removed rows.
  createApplicationUsingPost: (params: { application?: Partial<Application> } | Partial<Application>) => {
    const payload = (params as { application?: Partial<Application> })?.application ?? (params as Partial<Application>);
    return applicationStore.create(payload || {});
  },
  updateApplicationUsingPut: (params: { id?: string; application?: Partial<Application> } | Partial<Application>) => {
    const id = (params as { id?: string })?.id || (params as Application)?.id;
    const payload = (params as { application?: Partial<Application> })?.application ?? (params as Partial<Application>);
    return id ? applicationStore.update(id, payload || {}) : undefined;
  },
  deleteApplicationUsingDelete: (params: { id: string }) => applicationStore.remove(params.id),
  batchCreateResourcePoliciesUsingPost: () => ({ id: "mock-application-id" }),
  batchPublishPoliciesUsingPost: () => ({ success: true }),
  listResourceAccessPoliciesUsingGet: accessPolicies,

  // Demo-only: Unified Lakehouse Map Native Role / drift Deploy have no real
  // backend endpoint yet, so they keep mutating the mock lakehouseStore.
  // Throw on an unknown policy id rather than resolving `undefined` - the
  // mutation would otherwise report success (and toast it) for a policy it
  // never touched.
  mapLakehouseRoleUsingPost: (params: {
    policyId: string;
    dataSourceId: string;
    nativeRole?: string;
    autoCreate?: boolean;
  }) => {
    const updated = lakehouseStore.mapRole(params);
    if (!updated) {
      throw new Error(`Unknown mock lakehouse policy: ${params.policyId}`);
    }
    return updated;
  },
  deployLakehousePolicyUsingPost: (params: { policyId: string }) => {
    const deployed = lakehouseStore.deploy(params.policyId);
    if (!deployed) {
      throw new Error(`Unknown mock lakehouse policy: ${params.policyId}`);
    }
    return deployed;
  },

  // TrustAI Executive Decision Board: generated request shapes, plus mock-only methods.
  getEnterprisePostureUsingGet: () => trustAiExecutiveStore.getPosture(),
  listPriorityControlsUsingGet: () => trustAiExecutiveStore.listControls(),
  getEstateReadinessUsingGet: (params: { scenario?: string }) =>
    trustAiExecutiveStore.getEstateReadiness(params.scenario),
  startOnboardingRunUsingPost: (params: { body: { platformId: string } }) =>
    trustAiExecutiveStore.startOnboardingRun(params.body.platformId),
  startEvaluationUsingPost: () => trustAiExecutiveStore.startEvaluation(),
  getControlDecisionUsingGet: (params: { controlId: string }) =>
    trustAiExecutiveStore.getDecision(params.controlId),
  listAgentRegistryUsingGet: () => trustAiExecutiveStore.listAgentRegistry(),
  getVerifiedOutcomesUsingGet: (params: { controlId: string }) =>
    trustAiExecutiveStore.getVerifiedOutcomes(params.controlId),
  listAuditEvidenceUsingGet: () => trustAiExecutiveStore.listAuditEvidence(),
  enforcePolicyUsingPost: ({
    controlId,
    body,
  }: {
    controlId: string;
    body: Omit<EnforcePolicyParams, 'controlId'>;
  }) =>
    trustAiExecutiveStore.enforce(
      controlId,
      body.scopeId,
      body.agentCount,
      body.agentIds,
      body.includeFutureAgents ?? false,
    ),
  listRelevantIntelligenceUsingGet: () =>
    trustAiExecutiveStore.listRelevantIntelligence(),
  listGuardianRecommendationsUsingGet: () =>
    trustAiExecutiveStore.listGuardianRecommendations(),
  getEstateInventoryUsingGet: (params: { metricId: EstateMetricId }) =>
    trustAiExecutiveStore.getEstateInventory(params.metricId),
  getRelationshipGraphUsingGet: () => trustAiExecutiveStore.getRelationshipGraph(),
  getWhatsChangedUsingGet: () => trustAiExecutiveStore.getWhatsChanged(),
  listGuardianPromptsUsingGet: (params: {
    contextKey: string;
    contextKind: GuardianContextKind;
    label: string;
  }) =>
    trustAiExecutiveStore.listGuardianPrompts(
      params.contextKey,
      params.contextKind,
      params.label,
    ),
  getGuardianContextUsingGet: (params: {
    contextKey: string;
    contextKind: GuardianContextKind;
    label: string;
    metadata?: Record<string, unknown>;
  }) => trustAiExecutiveStore.getGuardianContext(params),
  askGuardianUsingPost: (params: {
    contextKey: string;
    contextKind: GuardianContextKind;
    label: string;
    metadata?: Record<string, unknown>;
    promptId: string | null;
    query: string;
  }) =>
    trustAiExecutiveStore.askGuardian(
      params.contextKey,
      params.contextKind,
      params.label,
      params.promptId,
      params.query,
      params.metadata,
    ),
  getDataGovernanceOverviewUsingGet: () =>
    trustAiExecutiveStore.getDataGovernanceOverview(),
  listDataPlatformGovernanceUsingGet: () =>
    trustAiExecutiveStore.listDataPlatformGovernance(),
  getDataPlatformGovernanceUsingGet: (params: { platformId: string }) =>
    trustAiExecutiveStore.getDataPlatformGovernance(params.platformId),
  getAgentFlowSummaryUsingGet: () => trustAiExecutiveStore.getAgentFlowSummary(),
  getDataGovernanceSummaryUsingGet: (params: {
    scenario?: DataGovernanceScenario;
  }) => buildDataGovernanceSummary(params.scenario),
  getAgentFleetProtectionUsingGet: (params: {
    scenario?: AgentGovernanceScenario;
    dimension?: FleetDimension;
  }) => buildAgentFleetProtection(params.scenario, params.dimension),
  getAgentActivitySummaryUsingGet: (params: {
    scenario?: AgentGovernanceScenario;
  }) => buildAgentActivitySummary(params.scenario),
  listAgentAccessFlowsUsingGet: (params: AgentAccessFlowListParams) =>
    trustAiExecutiveStore.listAgentAccessFlows(params),
  getAgentAccessFlowUsingGet: (params: { agentId: string }) =>
    trustAiExecutiveStore.getAgentAccessFlow(params.agentId),
};
